import axios from 'axios';
import debounce from 'debounce';

import ApiService from '@/core/services/api.service';

/** Khớp backend MIN_SEARCH_LENGTH */
export const MIN_PRODUCT_SEARCH_LENGTH = 3;
export const MAX_PRODUCT_SEARCH_LENGTH = 100;
/** Debounce autocomplete (ms) — plan: 300–500ms */
export const PRODUCT_SEARCH_DEBOUNCE_MS = 400;

const abortControllers = new Map();

export const resolveProductSearchText = (text) => {
  if (text === undefined || text === null || String(text).trim() === '') {
    return { mode: 'none', value: null };
  }
  const normalized = String(text).trim().slice(0, MAX_PRODUCT_SEARCH_LENGTH);
  if (normalized.length < MIN_PRODUCT_SEARCH_LENGTH) {
    return { mode: 'skip', value: null };
  }
  return { mode: 'search', value: normalized };
};

export const isProductSearchSkipped = (text) =>
  resolveProductSearchText(text).mode === 'skip';

export const isAbortError = (error) =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED' ||
  (typeof axios.isCancel === 'function' && axios.isCancel(error));

export const cancelProductSearch = (abortKey) => {
  if (!abortKey) return;
  const controller = abortControllers.get(abortKey);
  if (controller) {
    controller.abort();
    abortControllers.delete(abortKey);
  }
};

const emptyListResponse = () => ({ data: { data: [] } });

/**
 * GET product-search với AbortController + guard độ dài tối thiểu.
 * @param {string} resource - vd 'v1/product-search/for-sale'
 * @param {object} params - query params (searchText hoặc searchProduct/imeiCode/textSearch)
 * @param {object} options
 * @param {string} options.abortKey - key hủy request trước cùng scope
 * @param {string} [options.textParam='searchText'] - tên param text search
 */
export async function queryProductSearch(resource, params = {}, options = {}) {
  const { abortKey, textParam = 'searchText' } = options;
  const rawText = params[textParam];
  const resolved = resolveProductSearchText(rawText);

  if (resolved.mode === 'skip') {
    return emptyListResponse();
  }

  const queryParams = { ...params };
  if (resolved.mode === 'search') {
    queryParams[textParam] = resolved.value;
  } else if (textParam in queryParams) {
    delete queryParams[textParam];
  }

  if (abortKey) {
    cancelProductSearch(abortKey);
    const controller = new AbortController();
    abortControllers.set(abortKey, controller);
    try {
      return await ApiService.query(resource, {
        params: queryParams,
        signal: controller.signal,
      });
    } catch (error) {
      if (isAbortError(error)) {
        return emptyListResponse();
      }
      throw error;
    } finally {
      if (abortControllers.get(abortKey) === controller) {
        abortControllers.delete(abortKey);
      }
    }
  }

  return ApiService.query(resource, { params: queryParams });
}

export const unwrapProductSearchData = (response) => response?.data?.data || [];

export function searchProducts(params, options) {
  return queryProductSearch('v1/product-search', params, options);
}

export function searchProductsForSale(params, options) {
  return queryProductSearch('v1/product-search/for-sale', params, options);
}

export function searchByImei(params, options) {
  return queryProductSearch('v1/product-search/by-imei', params, options);
}

export function searchByImeiForSale(params, options) {
  return queryProductSearch('v1/product-search/by-imei-for-sale', params, options);
}

export function searchForChange(params, options) {
  return queryProductSearch('v1/product-search/for-change', params, {
    ...options,
    textParam: 'searchProduct',
  });
}

export function searchImeiForChange(params, options) {
  return queryProductSearch('v1/product-search/imei-for-change', params, {
    ...options,
    textParam: 'imeiCode',
  });
}

export function searchByCompany(params, options) {
  return queryProductSearch('v1/product-search/by-company', params, options);
}

export function searchForPrint(params, options) {
  return queryProductSearch('v1/product-search/for-print', params, {
    ...options,
    textParam: 'textSearch',
  });
}

export function searchImeiForPrint(params, options) {
  return queryProductSearch('v1/product-search/imei-for-print', params, {
    ...options,
    textParam: 'textSearch',
  });
}

/**
 * Debounce + abort cho vue-treeselect ASYNC_SEARCH.
 * executeFn(searchText, { abortKey }) => Promise<api response body { data: [] }>
 */
export function createDebouncedProductSearchExecutor(executeFn, debounceMs = PRODUCT_SEARCH_DEBOUNCE_MS) {
  const abortKey = `product-search-${Math.random().toString(36).slice(2, 10)}`;
  let latestSearchQuery = null;

  const debounced = debounce(async (searchQuery, callback) => {
    const requestQuery = String(searchQuery ?? '').trim();

    if (isProductSearchSkipped(requestQuery)) {
      if (latestSearchQuery === requestQuery) {
        callback?.(null, { data: [] });
      }
      return;
    }
    try {
      const result = await executeFn(requestQuery, { abortKey });
      if (latestSearchQuery === requestQuery) {
        callback?.(null, result);
      }
    } catch (error) {
      if (!isAbortError(error) && latestSearchQuery === requestQuery) {
        callback?.(error, { data: [] });
      }
    }
  }, debounceMs);

  return {
    run: (searchQuery, callback) => {
      latestSearchQuery = String(searchQuery ?? '').trim();
      cancelProductSearch(abortKey);
      debounced(searchQuery, callback);
    },
    cancel: () => {
      latestSearchQuery = null;
      debounced.clear?.();
      cancelProductSearch(abortKey);
    },
  };
}

export function validateBillProductSearchInput(productSearch, imeiSearch) {
  const productText = productSearch ? String(productSearch).trim() : '';
  if (productText && productText.length < MIN_PRODUCT_SEARCH_LENGTH) {
    return `Vui lòng nhập tìm kiếm sản phẩm tối thiểu ${MIN_PRODUCT_SEARCH_LENGTH} ký tự`;
  }
  const imeiText = imeiSearch ? String(imeiSearch).trim() : '';
  if (imeiText && imeiText.length < MIN_PRODUCT_SEARCH_LENGTH) {
    return `Vui lòng nhập tìm kiếm IMEI tối thiểu ${MIN_PRODUCT_SEARCH_LENGTH} ký tự`;
  }
  return null;
}
